import { Box, Grid, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useGetMainComponentById, useGetMainComponentROVVideos } from '../../../api/main-component'
import { CenteredSpinner } from '../../../components/Spinner'
// import ComponentCardView from './ComponentCardView'
import { AddCircleOutline, Edit } from '@mui/icons-material'
import { Context, FC, useCallback, useMemo, useState } from 'react'
import { useGetMooring } from 'src/api/moorings'
import { useGetPosition } from 'src/api/positions'
import { useGenerateSystemReports } from 'src/api/system/system-report'
import { invalidateDocumentCategory } from 'src/api/users/document-category'
import { ReactComponent as SendToServiceStationIcon } from 'src/assets/icons/serviceStationIcon.svg'
import { ReactComponent as DocumentDownload } from 'src/assets/svg/documentDownload.svg'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { GridActionButton } from 'src/components/common/GridActionButton'
import ROVContainer from 'src/components/ROVContainer'
import { MUIThemeColors } from 'src/mui-theme'
import { Padding } from 'src/styles/paddingComponent'
import { getThemeColor } from 'src/utils'
import { downloadFileLocal } from 'src/utils/downloadFile'
import { getCageLetter } from 'src/utils/function'
import { useGetLocality } from '../../../api/localities'
import NoDocument from '../../../assets/svg/no-equipment-documents.svg'
import { AuthorizationProvider } from '../../../components/common/Authorization'
import OverallReport from '../../../components/overallReport/OverallReport'
import { Spinner } from '../../../components/Spinner'
import { IMainComponentType, MainComponentStatus, SystemReportTypes } from '../../../enums'
import { AuthorizationType } from '../../../enums/auth'
import { permissionValue } from '../../../enums/permissionValue'
import { IDeviationType } from '../../../generated-types/deviation-type'
import { formFieldsDefaultValues } from '../../../generated-types/form-field'
import { formSchemaReportTypes } from '../../../generated-types/form-schema'
import { IDeviationFormWizardType } from '../../../generated-types/form-wizard'
import { useDialogCtx } from '../../../hooks/context-hooks'
import { UpdateMainComponentDialog } from '../../customer/mainComponents/UpdateMainComponentDialog'
import { CreateCertificateDialog } from '../Certificates/Components/CertificatesContainer'
import { CreatePositionDeviationDialog, ICreateDeviationDialogProps } from '../deviations/CreatePositionDeviationDialog'
import { AccordionDocumentsTable } from '../documents/components/AccordionDocumentsTable'
import { CreateDocumentDialog, ICreateDocumentsParams } from '../documents/components/CreateDocumentDialog'
import { MainComponentSendModalComponent } from '../positions/components/MainComponentSendModal'
import { BulkUploadRovDialog } from '../positions/components/BulkUploadRovDialog'
import ChangeCageDialog from './ChangeCageDialog'
import CertificateTab from './components/CertificateTab'
import DeviationTab from './components/DeviationTab'
import ImpregnationTab from './components/ImpregnationTab'
import { MainComponentHistoriesTable } from './MainComponentHistoriesTable'
import MainComponentEvents from './MainComponentEvents'
import WashingReports from './WashingReports'

export interface IMainComponentContainerProps {
  context?: Context<any> | null
  permission?: number
}

export const MainComponentContainer: FC<IMainComponentContainerProps> = ({ context, permission }) => {
  const { t } = useTranslation(['main-component', 'common', 'equipment'])
  const { customerId: cId, id: adminCustomerId, localityId, mainComponentId, serviceStationId } = useParams()
  const customerId = useMemo(() => cId ?? adminCustomerId, [cId, adminCustomerId])
  const { data, isLoading } = useGetMainComponentById(customerId as EntityId, mainComponentId as EntityId)
  const { data: locality } = useGetLocality(
    customerId as EntityId,
    data?.selectedLocalityId ? (data?.selectedLocalityId as EntityId) : (0 as EntityId),
    { mainComponentId },
  )
  const { data: mooringDetails, isLoading: mooringLoading } = useGetMooring(
    customerId as EntityId,
    localityId || (data?.selectedLocalityId as EntityId),
    data?.position?.mooringId as EntityId,
    { isWithAllInfo: true },
  )
  const { data: positionDetail, isLoading: positionLoading } = useGetPosition(
    customerId as EntityId,
    data?.selectedLocalityId as EntityId,
    data?.position?.mooringId as EntityId,
    data?.positionId as EntityId,
  )
  const { mutateAsync: generateAsync, isLoading: isGenerating } = useGenerateSystemReports()
  const { showDialog } = useDialogCtx()
  const theme = useTheme()
  const { localityId: localityIdFromURL } = useParams()
  const [selectedCategoryId, setSelectedCategoryId] = useState<EntityId | null>(null)
  const { data: rovVideosWithSharedCategories } = useGetMainComponentROVVideos(
    customerId as EntityId,
    mainComponentId as EntityId,
  )

  const onUpdate = useCallback(
    async id => {
      const added = await showDialog(
        UpdateMainComponentDialog,
        {
          componentProps: {
            id: id as EntityId,
            mainComponentTypeName: data?.type.name as string,
            customerId: customerId as EntityId,
            localityId: localityIdFromURL as EntityId,
          },
        },
        undefined,
        true,
      )
      if (!added) return
    },
    [data?.mainComponentTypeName],
  )

  const onChangeCage = useCallback(
    id => {
      showDialog(
        ChangeCageDialog,
        {
          componentProps: {
            id: id as EntityId,
            customerId: customerId as EntityId,
            localityId: locality?.id as EntityId,
            positionId: data?.positionId as EntityId,
            mooringId: data?.position?.mooringId as EntityId,
          },
          opts: {
            maxWidth: 'sm',
            fullWidth: true,
          },
        },
        undefined,
        true,
      )
    },
    [locality, customerId, data?.positionId],
  )

  const onAddDeviationForComponent = useCallback(async () => {
    await showDialog(
      CreatePositionDeviationDialog,
      {
        componentProps: {
          customerId: customerId as EntityId,
          localityId: locality?.id as EntityId,
          mooringId: data?.position?.mooringId as EntityId,
          position: data?.position,
          mainComponent: data,
          formWizard: IDeviationFormWizardType.Component,
          type:
            data?.type.componentType === IMainComponentType.Custom
              ? IDeviationType.Equipment
              : IDeviationType.MainComponent,
          disableSteps: true,
        } as ICreateDeviationDialogProps,
        isAutoCloseDisabled: true,
      },
      undefined,
      true,
    )
  }, [customerId, locality?.id, data])

  const onAddCertificateForComponent = async () => {
    const added = await showDialog(
      CreateCertificateDialog,
      {
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
        componentProps: {
          mainComponentId: mainComponentId as EntityId,
          customerId: customerId as EntityId,
        },
      },
      undefined,
      true,
    )
    if (!added) return
  }

  const onAddDocumentForComponent = async () => {
    const documentParams = {
      mainComponentId: mainComponentId as EntityId,
      mooringId: data?.position?.mooringId as EntityId,
      positionId: data?.positionId,
      mooringName: data?.position?.mooringName as string,
    } as ICreateDocumentsParams
    const added = await showDialog(
      CreateDocumentDialog,
      {
        opts: {
          maxWidth: 'md',
          fullWidth: true,
        },
        componentProps: {
          customerId: customerId as EntityId,
          localityId: locality?.id as EntityId,
          createDocumentsParams: documentParams,
        },
        pop: () => {
          invalidateDocumentCategory.getDocumentCategoriesWithDocuments(customerId as EntityId, { mainComponentId })
        },
      },
      undefined,
      true,
    )
    if (!added) return
  }

  const onSend = async () => {
    const updated = await showDialog(MainComponentSendModalComponent, {
      title: t('sendTo', { ns: 'main-component' }),
      componentProps: {
        customerId: customerId as EntityId,
        localityId: locality?.id as EntityId,
        mooringId: data?.position?.mooringId as EntityId,
        positionId: data?.positionId as EntityId,
        id: mainComponentId as EntityId,
        localityIdFromURL: localityIdFromURL,
        onlyServiceStation: false,
      },
      opts: {
        maxWidth: 'sm',
        fullWidth: true,
      },
    })
    if (!updated) return
  }

  const onAddRovVideo = async () => {
    const added = await showDialog(
      BulkUploadRovDialog,
      {
        componentProps: {
          customerId: customerId as EntityId,
          localityId: localityId as EntityId,
          mainComponentId: mainComponentId as EntityId,
          equipmentOnly: !localityId,
          rovVideosWithSharedCategories: rovVideosWithSharedCategories,
          selectedCategoryId: selectedCategoryId,
        },
        opts: {
          maxWidth: 'lg',
          fullWidth: true,
        },
        isAutoCloseDisabled: true,
      },
      undefined,
      true,
    )

    if (!added) return
  }

  const onGenerateReport = async () => {
    await generateAsync(
      {
        customerId: customerId,
        equipmentId: mainComponentId,
        reportId: SystemReportTypes.EquipmentReport,
      },
      {
        onSuccess: data => {
          downloadFileLocal(data, 'equipment-report')
        },
      },
    )
  }

  if (isLoading) return <CenteredSpinner />
  const mainComponentServiceStationStatus = [
    MainComponentStatus.Incoming,
    MainComponentStatus.InService,
    MainComponentStatus.InStock,
    MainComponentStatus.Outgoing,
  ]

  if (data !== undefined)
    return (
      <CommonPageLayout
        titleSection={{
          title: t('main-component-details', { ns: 'main-component' }),
        }}
        topAction={[
          <Box key="actions" className="flex items-center justify-end px-4" borderRadius={2}>
            <GridActionButton
              buttonIcon={
                isGenerating ? <Spinner size={'24px'} /> : <DocumentDownload height={'24px'} width={'24px'} />
              }
              onClick={async () => {
                onGenerateReport()
              }}
              context={context}
              variant={'outlined'}
              key="report"
              shadowWeight={null}
              width="auto"
            />
            {data?.positionId != null && (
              <Padding.pl16>
                <AuthorizationProvider
                  permissions={[
                    permissionValue.Locality.Mooring.Write,
                    permissionValue.ServiceStation.Write,
                    permissionValue.ClientAdminPermission,
                  ]}
                  Context={context}
                  type={AuthorizationType.Disabled}
                >
                  <GridActionButton
                    onClick={() => onChangeCage(data.id)}
                    startIcon={<img height={'28px'} width={'28px'} src="/icons/change.svg" />}
                    buttonText={t('change-cage', { ns: 'common' })}
                    height="44px"
                  />
                </AuthorizationProvider>
              </Padding.pl16>
            )}
            {((data?.selectedLocalityId && data.status === MainComponentStatus.InStock) ||
              !(data && mainComponentServiceStationStatus.includes(data.status))) && (
                <Padding.pl16>
                  <AuthorizationProvider
                    permissions={[
                      permissionValue.Locality.Mooring.Write,
                      permissionValue.ServiceStation.Write,
                      permissionValue.ClientAdminPermission,
                    ]}
                    Context={context}
                    type={AuthorizationType.Disabled}
                  >
                    <GridActionButton
                      onClick={() => onSend()}
                      startIcon={<SendToServiceStationIcon style={{ height: 28, width: 28 }} aria-hidden />}
                      buttonText={t('sendTo', { ns: 'main-component' })}
                      height="44px"
                    />
                  </AuthorizationProvider>
                </Padding.pl16>
              )}
            <Padding.pl16>
              <AuthorizationProvider
                permissions={[
                  permissionValue.Locality.Mooring.Write,
                  permissionValue.ServiceStation.Write,
                  permissionValue.ClientAdminPermission,
                ]}
                Context={context}
                type={AuthorizationType.Disabled}
              >
                <GridActionButton
                  buttonIcon={<Edit />}
                  onClick={() => onUpdate(data.id)}
                  toolTipTitle={t('edit', { ns: 'common' })}
                  height="44px"
                  width="44px"
                />
              </AuthorizationProvider>
            </Padding.pl16>
          </Box>,
        ]}
      >
        <AuthorizationProvider
          type={AuthorizationType.Disabled}
          permissions={[
            permissionValue.ServiceStation.Read,
            permissionValue.Locality.MainComponent.Certificate.Read,
            permissionValue.Locality.MainComponent.Certificate.Write,
            permissionValue.Locality.MainComponent.Certificate.Delete,
            permissionValue.ClientAdminPermission,
          ]}
          Context={context}
        >
          <Padding.pt16>
            <CertificateTab
              mainComponentId={data.id}
              positionId={data.positionId}
              customerId={customerId as EntityId}
              onAddCertificateForComponent={onAddCertificateForComponent}
              Context={context}
              mainComponentType={data?.type}
              mainComponentStatus={data.status}
            />
          </Padding.pt16>
        </AuthorizationProvider>
        <AuthorizationProvider
          type={AuthorizationType.Disabled}
          Context={context}
          permissions={[
            permissionValue.ServiceStation.Read,
            permissionValue.Locality.MainComponent.Deviation.Read,
            permissionValue.Locality.MainComponent.Deviation.Write,
            permissionValue.Locality.MainComponent.Deviation.Delete,
            permissionValue.ClientAdminPermission,
          ]}
        >
          <DeviationTab
            customerId={customerId!}
            localityId={localityId}
            mainComponentId={data.id}
            onAddDeviationForComponent={onAddDeviationForComponent}
            queryParams={{ mainComponentId }}
            context={context as Context<any>}
            addPermissions={[
              permissionValue.Locality.MainComponent.Deviation.Write,
              permissionValue.ServiceStation.Write,
              permissionValue.ClientAdminPermission,
            ]}
            updatePermissions={[
              permissionValue.Locality.MainComponent.Deviation.Write,
              permissionValue.ServiceStation.Write,
              permissionValue.ClientAdminPermission,
            ]}
            deletePermissions={[
              permissionValue.Locality.MainComponent.Deviation.Delete,
              permissionValue.ServiceStation.Delete,
              permissionValue.ClientAdminPermission,
            ]}
            permission={permission}
            emptyStatusTitle={t('main-component-deviation-empty-status-title', { ns: 'main-component' })}
            emptyStatusContent={t('main-component-deviation-empty-status-content', { ns: 'main-component' })}
            emptyStatusTitleFontSize="14px"
            emptyStatusContentFontSize="10px"
            emptyStatusImageWidth="60px"
          />
        </AuthorizationProvider>
        <Grid container>
          <Grid md={6} className="pr-8-core">
            <CommonPageLayout
              titleSection={{ subtitle: t('documents', { ns: 'document' }) }}
              topAction={[
                {
                  buttonText: t('documents', { ns: 'document' }),
                  buttonIcon: <AddCircleOutline />,
                  onClick: onAddDocumentForComponent,
                  permissionBinaryValues: [
                    permissionValue.Locality.MainComponent.Document.Write,
                    permissionValue.ClientAdminPermission,
                    permissionValue.ServiceStation.Write,
                  ],
                  variant: 'outlined',
                  shadowWeight: null,
                  context: context,
                },
              ]}
            >
              <Padding.p6
                borderRadius={'4px'}
                border={`1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`}
              >
                <AccordionDocumentsTable
                  customerId={customerId as EntityId}
                  localityId={locality?.id as EntityId}
                  documentTypeId={''}
                  documentsParams={{ mainComponentId, customerId: customerId as EntityId }}
                  height={window.innerHeight / 3}
                  context={context as Context<any>}
                  emptyStatusTitle={t('equipment-document-empty-status-title', { ns: 'equipment' })}
                  emptyStatusContent={t('equipment-document-empty-status-content', { ns: 'equipment' })}
                  emptyStatusImageWidth="122px"
                  emptyStatusHeight="150px"
                  emptyStatusImage={NoDocument}
                  emptyStatusContentFontSize="14px"
                  emptyStatusTitleFontSize="18px"
                  equipmentStatus={data.status}
                />
              </Padding.p6>
            </CommonPageLayout>
          </Grid>
          <Grid md={6} className="pl-8-core">
            <AuthorizationProvider
              type={AuthorizationType.Disabled}
              Context={context}
              permissions={[
                permissionValue.ServiceStation.Read,
                permissionValue.Locality.MainComponent.Report.Read,
                permissionValue.ClientAdminPermission,
              ]}
            >
              <OverallReport
                type={formSchemaReportTypes.MainComponent}
                mainComponentId={mainComponentId}
                context={context}
                customerId={customerId as EntityId}
                formInitialValues={{
                  [formFieldsDefaultValues.orderNumber]: data?.orderNumber || '',
                  [formFieldsDefaultValues.netType]:
                    Object.keys(IMainComponentType)[
                    Object.values(IMainComponentType).indexOf(
                      data?.type.componentType as unknown as IMainComponentType,
                    )
                    ] || '',
                  [formFieldsDefaultValues.siteLocationName]: locality?.name ? locality?.name : 'n/a',
                  [formFieldsDefaultValues.facilityName]: locality?.name ? locality?.name : 'n/a',
                  [formFieldsDefaultValues.siteNumber]: locality?.locationNr ? locality?.locationNr : 'n/a',
                  [formFieldsDefaultValues.resourcesName]: data?.name || '',
                  [formFieldsDefaultValues.PositionNumber]: data?.position?.id?.toString() || '',
                  [formFieldsDefaultValues.cagePlacement]: `${getCageLetter(data?.position?.name ?? '')} ${data?.position?.reference ? `(${data?.position?.reference})` : ''
                    }`,
                }}
                createPermissions={[
                  permissionValue.Locality.MainComponent.Report.Write,
                  permissionValue.ServiceStation.Write,
                  permissionValue.ClientAdminPermission,
                ]}
                deletePermissions={[
                  permissionValue.Locality.MainComponent.Report.Delete,
                  permissionValue.ServiceStation.Delete,
                  permissionValue.ClientAdminPermission,
                ]}
                height={window.innerHeight / 3}
                listView={true}
                emptyStatusImageWidth="107px"
                emptyStatusContent={t('reports-empty-status-content', { ns: 'equipment' })}
                emptyStatusTitle={t('reports-empty-status-title', { ns: 'equipment' })}
                emptyStatusTitleFontSize="18px"
                emptyStatusContentFontSize="14px"
              />
            </AuthorizationProvider>
          </Grid>
        </Grid>
        <CommonPageLayout
          titleSection={{ subtitle: t('rovMovies', { ns: 'rov' }) }}
          topAction={[
            {
              buttonText: t('rov-movie', { ns: 'rov' }),
              buttonIcon: <AddCircleOutline />,
              onClick: onAddRovVideo,
              permissionBinaryValues: [
                permissionValue.Locality.MainComponent.Document.Write,
                permissionValue.ClientAdminPermission,
                permissionValue.ServiceStation.Write,
              ],
              variant: 'outlined',
              shadowWeight: null,
              context: context,
            },
          ]}
          titleContainerProps={{ paddingBottom: '12px' }}
        >
          <Box
            padding={'8px'}
            bgcolor={getThemeColor(theme, MUIThemeColors.white)}
            borderRadius={'4px'}
            height={window.innerHeight / 3}
            sx={{ overflowY: 'auto' }}
          >
            <ROVContainer
              rovVideosWithSharedCategories={rovVideosWithSharedCategories}
              width={'100%'}
              localityId={localityId}
              customerId={customerId}
              mooringId={data?.position?.mooringId as EntityId}
              positionId={data?.position?.id}
              mainComponentId={mainComponentId as EntityId}
              onCategoryChange={setSelectedCategoryId}
            />
          </Box>
        </CommonPageLayout>
        <MainComponentEvents mainComponentId={mainComponentId as EntityId} context={context} />
        <WashingReports mainComponentId={mainComponentId as EntityId} context={context} positionId={data?.positionId} />
        {data.type.componentType == IMainComponentType.Net && (
          <ImpregnationTab
            mainComponentId={mainComponentId as EntityId}
            context={context}
            mainComponentStatus={data.status}
            serviceStationId={serviceStationId as EntityId}
          />
        )}
        <CommonPageLayout titleSection={{ subtitle: t('activity-log', { ns: 'common' }) }} topAction={[]}>
          <MainComponentHistoriesTable
            mainComponentId={mainComponentId as EntityId}
            customerId={customerId as EntityId}
            localityId={localityId || (data?.selectedLocalityId as EntityId)}
            equipmentLocalityId={data?.selectedLocalityId as EntityId | undefined}
            mooringId={data?.position?.mooringId as EntityId}
          />
        </CommonPageLayout>
      </CommonPageLayout>
    )
  return <></>
}
